import { WebhookEndpoint, WebhookEventDataType } from '../types/types';

export type EventDeliveryDiffField =
  'endpointUrl' | 'maskedSecret' | 'customRequestHeaders' | 'failureNotificationEmails';

export type EventDeliveryDiff = {
  field: EventDeliveryDiffField;
  label: string;
  eventValue: string;
  subscriptionValue: string;
};

const normalizeHeaders = (headers?: Record<string, string> | null): Record<string, string> =>
  headers ?? {};

const headersEqual = (
  left?: Record<string, string> | null,
  right?: Record<string, string> | null,
): boolean => {
  const normalizedLeft = normalizeHeaders(left);
  const normalizedRight = normalizeHeaders(right);
  const leftKeys = Object.keys(normalizedLeft).sort();
  const rightKeys = Object.keys(normalizedRight).sort();

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => normalizedLeft[key] === normalizedRight[key]);
};

const formatHeaders = (headers?: Record<string, string> | null): string => {
  const normalized = normalizeHeaders(headers);
  const keys = Object.keys(normalized);

  if (keys.length === 0) {
    return 'None';
  }

  return keys
    .sort()
    .map((key) => `${key}: ${normalized[key]}`)
    .join(', ');
};

const formatSecret = (maskedSecret?: string): string => maskedSecret ?? 'None';

const normalizeEmailList = (emails?: string[] | null): string[] =>
  [...(emails ?? [])].map((email) => email.trim().toLowerCase()).sort();

const emailsEqual = (left?: string[] | null, right?: string[] | null): boolean => {
  const normalizedLeft = normalizeEmailList(left);
  const normalizedRight = normalizeEmailList(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((email, index) => email === normalizedRight[index]);
};

const formatEmails = (emails?: string[] | null): string => {
  const normalized = emails?.filter(Boolean) ?? [];

  if (normalized.length === 0) {
    return 'None';
  }

  return normalized.join(', ');
};

/** Returns the delivery fields that differ between the event snapshot and live subscription. */
export const getEventDeliveryDiffs = (
  event: WebhookEventDataType,
  subscription: WebhookEndpoint,
): EventDeliveryDiff[] => {
  const diffs: EventDeliveryDiff[] = [];

  if (event.endpointUrl !== subscription.endpoint) {
    diffs.push({
      field: 'endpointUrl',
      label: 'Endpoint URL',
      eventValue: event.endpointUrl,
      subscriptionValue: subscription.endpoint,
    });
  }

  if ((event.maskedSecret ?? '') !== (subscription.maskedSecret ?? '')) {
    diffs.push({
      field: 'maskedSecret',
      label: 'Secret',
      eventValue: formatSecret(event.maskedSecret),
      subscriptionValue: formatSecret(subscription.maskedSecret),
    });
  }

  if (!headersEqual(event.customRequestHeaders, subscription.customRequestHeaders)) {
    diffs.push({
      field: 'customRequestHeaders',
      label: 'Custom request headers',
      eventValue: formatHeaders(event.customRequestHeaders),
      subscriptionValue: formatHeaders(subscription.customRequestHeaders),
    });
  }

  if (!emailsEqual(event.failureNotificationEmails, subscription.failureNotificationEmails)) {
    diffs.push({
      field: 'failureNotificationEmails',
      label: 'Failure notification emails',
      eventValue: formatEmails(event.failureNotificationEmails),
      subscriptionValue: formatEmails(subscription.failureNotificationEmails),
    });
  }

  return diffs;
};

/** Returns true when delivery-relevant event snapshot fields differ from the live subscription. */
export const eventDeliveryDiffersFromSubscription = (
  event: WebhookEventDataType,
  subscription: WebhookEndpoint,
): boolean => getEventDeliveryDiffs(event, subscription).length > 0;
